import { useCallback, useMemo, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData } from '@tanstack/react-query'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { $api } from '../api/queryClient'
import { useAssetTypes } from '../api/queries'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { formatAssetWithPath } from '../lib/assetPath'
import { useDebounce } from '../hooks/useDebounce'
import { getLocalizedName } from '../lib/localization'
import type { Asset } from '../hooks/useQueries'

// The list endpoint doesn't declare `parent_chain` on its Asset schema (it is
// only ever added at runtime by `asset_chain=true`, see
// `useAssetWithParentChain`), so extend the shared `Asset` type locally
// rather than casting.
type PickerAsset = Asset & { parent_chain?: boolean }

/**
 * Guard for a Dialog's `onEscapeKeyDown`. Radix's `DismissableLayer` (behind
 * the shadcn `Dialog`) listens for Escape on `document` with
 * `{capture: true}`, so it runs BEFORE this component's own Input
 * `onKeyDown` (bubble phase) and would close the dialog before the picker's
 * Escape-to-clear ever fires — same root cause `IssueSheet` guards against
 * for its inline edits. A caller that hosts `AssetPicker` inside a `Dialog`
 * must wire this into `onEscapeKeyDown`:
 *
 * ```tsx
 * <DialogContent onEscapeKeyDown={(e) => { if (isAssetPickerEscapeToClear(e)) e.preventDefault() }}>
 * ```
 *
 * Only guards when the picker's own input is focused AND has a non-empty
 * term — an empty term must still let Escape close the dialog, so this never
 * traps the user.
 */
export function isAssetPickerEscapeToClear(event: Pick<Event, 'target'>): boolean {
    const target = event.target as HTMLInputElement | null
    return Boolean(target?.dataset?.assetPickerInput === 'true' && target.value)
}

/**
 * A minimal "pick one asset" control: a search box over the org's assets and a
 * short result list. Used by the invite dialog when a scoped role is chosen —
 * a grant needs an anchor, and typing a name is how a manager names one
 * (spec 2026-09-04-03) — and by the conversation panel (attach / promote) and
 * the intake-addresses dialog to name a site.
 *
 * Every caller gets the same explicit states (spec 2026-09-09-06): a hint
 * under 2 characters, a small inline spinner while the debounced request is
 * in flight, an empty line when it resolves to nothing, and an inline error
 * line if it fails. Each result row's first line is the name followed by its
 * full ancestor path from the root, `Chambre 1 (Capitul, 2e, App 222)`
 * (`formatAssetWithPath`), and the muted second line is the type label alone.
 * The path is built client-side from `asset_chain=true` rows. Ancestor rows
 * (`parent_chain: true`) are dropped from the selectable list.
 *
 * Scoped search (spec 2026-10-10-02): `scopeUid` restricts the search to the
 * descendants of a place (`ancestor_uid`) and shows an "In: <scopeLabel>" chip
 * with a "Search everywhere" button that drops the scope for this picker's
 * session. The search never widens by itself: an empty scoped result shows the
 * empty line plus the same button. `assetTypeCodes` (`asset_type_code`) is a
 * caller constraint with no way out for the user. The backend still returns
 * the ancestors above the scope, so the path starts at the root.
 *
 * `limit: 10` is applied by the backend to the actual search matches BEFORE
 * it appends the parent-chain rows (`internal/handlers/assets/service.go`
 * `ListAssets`), so up to 10 real matches always come back regardless of how
 * many extra ancestor rows ride along — no need to raise the request limit.
 *
 * Deliberately not a full tree browser: the anchor is almost always a lot or
 * a building whose name the manager already knows, and a tree would make the
 * dialog taller than whatever it serves.
 */
export function AssetPicker({
    org,
    value,
    valueLabel,
    onChange,
    testId = 'asset-picker',
    placeholder,
    help,
    scopeUid,
    scopeLabel,
    assetTypeCodes,
}: {
    org: string
    value?: string
    valueLabel?: string
    /** `pathLabel` is the row's first line (`Chambre 1 (Capitul, 2e, App 222)`), handy as the next `valueLabel`. */
    onChange: (uid: string, name: string, pathLabel?: string) => void
    testId?: string
    /** Overrides the default search placeholder (`common:asset_picker.placeholder`). */
    placeholder?: string
    /** Caller-specific explanation shown under the picker. Nothing renders when omitted — no caller should end up with an empty help line by accident. */
    help?: string
    /** Restrict the search to the descendants of this asset (`ancestor_uid`). The user can widen it with "Search everywhere". */
    scopeUid?: string
    /** Name shown in the "In: <scopeLabel>" chip. Falls back to nothing but the generic wording when omitted. */
    scopeLabel?: string
    /** Caller constraint on the asset type codes (`asset_type_code`). Not removable by the user. */
    assetTypeCodes?: string[]
}) {
    const { t, i18n } = useTranslation('common')
    const [term, setTerm] = useState('')
    const [widened, setWidened] = useState(false)
    const activeScope = scopeUid && !widened ? scopeUid : undefined
    const debounced = useDebounce(term, 250)
    const trimmedTerm = term.trim()
    const debouncedTrimmed = debounced.trim()
    const queryEnabled = Boolean(org) && debouncedTrimmed.length >= 2

    const { data, isFetching, isError } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets',
        {
            params: {
                path: { orgCode: org },
                query: {
                    q: debouncedTrimmed,
                    limit: 10,
                    page: 1,
                    asset_chain: true,
                    sort: 'relevance',
                    ...(activeScope ? { ancestor_uid: activeScope } : {}),
                    ...(assetTypeCodes && assetTypeCodes.length > 0 ? { asset_type_code: assetTypeCodes.join(',') } : {}),
                },
            },
        },
        {
            enabled: queryEnabled,
            // Keep the previous list on screen while a new debounced term is
            // in flight — only the small spinner below signals the refetch.
            placeholderData: keepPreviousData,
            retry: false,
        }
    )

    const { data: assetTypes } = useAssetTypes(org)
    const assetTypesByUid = useMemo(() => {
        const m = new Map<string, { name: string | Record<string, string> }>()
        for (const at of assetTypes ?? []) m.set(at.uid, at)
        return m
    }, [assetTypes])

    const allRows = useMemo(() => (data?.data ?? []) as PickerAsset[], [data])
    const byUid = useMemo(() => {
        const m = new Map<string, PickerAsset>()
        for (const a of allRows) m.set(a.uid, a)
        return m
    }, [allRows])

    // Rows added only to complete the chain are never selectable — they are
    // raw material for the context line below, not results of their own.
    const results = useMemo(() => allRows.filter((a) => !a.parent_chain).slice(0, 10), [allRows])

    const typeLabel = useCallback(
        (asset: PickerAsset): string => {
            const type = asset.asset_type_uid ? assetTypesByUid.get(asset.asset_type_uid) : undefined
            return type ? (typeof type.name === 'string' ? type.name : getLocalizedName(type.name, i18n.language)) : ''
        },
        [assetTypesByUid, i18n.language]
    )

    const handleSelect = useCallback(
        (asset: PickerAsset) => {
            onChange(asset.uid, asset.name ?? '', formatAssetWithPath(asset, byUid))
            setTerm('')
        },
        [onChange, byUid]
    )

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Cheap keyboard courtesy, not a combobox rewrite: Enter only ever
        // resolves an unambiguous single match.
        if (event.key === 'Enter' && results.length === 1) {
            event.preventDefault()
            handleSelect(results[0])
            return
        }
        if (event.key === 'Escape' && term) {
            event.preventDefault()
            setTerm('')
        }
    }

    const showHint = trimmedTerm.length === 1
    const showLoading = queryEnabled && isFetching
    const showError = queryEnabled && isError
    const showEmpty = queryEnabled && !isFetching && !isError && results.length === 0
    const showResults = queryEnabled && !isError && results.length > 0

    return (
        <div className="space-y-2" data-testid={testId}>
            <div className="relative">
                <Input
                    placeholder={placeholder ?? t('asset_picker.placeholder')}
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    onKeyDown={handleKeyDown}
                    data-testid={`${testId}-input`}
                    data-asset-picker-input="true"
                />
                {showLoading && (
                    <AppIcon path={mdiLoading} size="sm" className="absolute right-2 top-1/2 -translate-y-1/2 motion-safe:animate-spin" aria-label={t('asset_picker.loading')} data-testid={`${testId}-loading`} />
                )}
            </div>
            {scopeUid && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid={`${testId}-scope`}>
                    {!widened ? (
                        <>
                            <span className="rounded-full border px-2 py-0.5" data-testid={`${testId}-scope-chip`}>
                                {t('asset_picker.scope_in', { scope: scopeLabel ?? '' })}
                            </span>
                            <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs"
                                onClick={() => setWidened(true)}
                                data-testid={`${testId}-search-everywhere`}
                            >
                                {t('asset_picker.search_everywhere')}
                            </Button>
                        </>
                    ) : null}
                </div>
            )}
            {value && valueLabel && (
                <p className="text-xs text-muted-foreground" data-testid={`${testId}-selected`}>
                    {valueLabel}
                </p>
            )}
            {showHint && (
                <p className="text-xs text-muted-foreground" data-testid={`${testId}-hint`}>
                    {t('asset_picker.min_chars')}
                </p>
            )}
            {showError && (
                <p className="text-xs text-destructive" data-testid={`${testId}-error`}>
                    {t('asset_picker.error')}
                </p>
            )}
            {showEmpty && (
                <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
                    {t('asset_picker.empty', { term: debouncedTrimmed })}
                    {activeScope && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="ml-1 h-auto p-0 text-xs"
                            onClick={() => setWidened(true)}
                            data-testid={`${testId}-empty-search-everywhere`}
                        >
                            {t('asset_picker.search_everywhere')}
                        </Button>
                    )}
                </p>
            )}
            {showResults && (
                <ul className="max-h-40 divide-y overflow-y-auto rounded-md border">
                    {results.map((asset) => {
                        const context = typeLabel(asset)
                        return (
                            <li key={asset.uid}>
                                <button
                                    type="button"
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-muted ${
                                        asset.uid === value ? 'bg-muted font-medium' : ''
                                    }`}
                                    data-testid={`${testId}-option-${asset.uid}`}
                                    onClick={() => handleSelect(asset)}
                                >
                                    <div>
                                        {formatAssetWithPath(asset, byUid)}
                                    </div>
                                    {context && <div className="text-xs font-normal text-muted-foreground">{context}</div>}
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
            {help && <p className="text-xs text-muted-foreground">{help}</p>}
        </div>
    )
}
